import React, { useCallback, useMemo, useState } from "react"
import { ButtonPrimary, ButtonTertiary, ButtonWorkspaceDropdown } from "@nova/component-btn"
import { useTranslation } from "@nova/use-translation"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectActiveWorkspace,
    selectTagById,
    selectLegacyWorkspaceTree,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { convertDataTypeToInviteDestination } from "./module"
import {
    selectRequestAccessModalId,
    setRequestModalId,
    updateWorkspaceMemberAccessRequest,
} from "features/entity-caching/members/UserManagementSlice"
import { selectUserID } from "user/profile/selectors"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import * as Icons from "@nova/component-icons"
import { findTagAncestors } from "features/data-management/workspace/module"
import { CopyInviteLinkButton } from "./CopyInviteLinkButton"
import { hideGlobalModal } from "runlevel/actions"

export const RequestAccessModal = () => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()

    const selfId = useAppSelector(selectUserID) || ""

    const requestModalId = useAppSelector(selectRequestAccessModalId())
    const activeWorkspace = useAppSelector(selectActiveWorkspace())
    const sidebarTree = useAppSelector(selectLegacyWorkspaceTree)

    const activeTag = useAppSelector(selectTagById(requestModalId || ""))

    const activeProjectId = useAppSelector(selectActivatedFireId)
    const activeProject = useAppSelector(selectFireById(requestModalId || activeProjectId))
    const filteredTags = findTagAncestors(sidebarTree || [], activeProject?.tagId || activeTag?.id)

    const [dropdownOpen, setDropdownOpen] = useState(false)

    const subDestinations: { id: string; fullName: string; icon: Icons.IconComponent }[] = []
    const reversedTags = useMemo(() => filteredTags.reverse(), [filteredTags])

    if (activeTag)
        subDestinations.push({
            ...convertDataTypeToInviteDestination(activeTag),
            icon: Icons.Folder,
        })
    if (activeProject)
        subDestinations.push({
            ...convertDataTypeToInviteDestination(activeProject),
            icon: Icons.Pen4,
        })
    reversedTags.forEach((tag) => {
        if (tag.id !== activeTag?.id)
            subDestinations.push({ ...convertDataTypeToInviteDestination(tag), icon: Icons.Folder })
    })

    const inviteDestinations = {
        workspace: activeWorkspace,
        subDestinations: subDestinations,
    }

    const selectorItems = [...inviteDestinations.subDestinations]
    if (inviteDestinations.workspace)
        selectorItems.push({
            ...convertDataTypeToInviteDestination(inviteDestinations.workspace),
            icon: Icons.Language,
        })

    const dropdownButtons = selectorItems.map((item) => {
        return {
            label: item.fullName,
            onClick: () => setRequestDestination(item),
            icon: item.icon,
        }
    })

    const [requestDestination, setRequestDestination] = useState<{
        id: string
        fullName: string
        icon: Icons.IconComponent
    } | null>(selectorItems[0])

    const handleModalClose = useCallback(() => {
        dispatch(hideGlobalModal())
        dispatch(setRequestModalId())
    }, [dispatch])

    const handleAccessRequest = useCallback(() => {
        if (!requestDestination || !activeWorkspace) return
        dispatch(
            updateWorkspaceMemberAccessRequest({
                workspaceId: activeWorkspace.id,
                userId: selfId,
                accessRequestId: requestDestination.id,
            })
        )
        dispatch(
            setGlobalNotificationContext({
                message: "Your access request has been sent to the admin(s).",
                status: GlobalNotificationStatus.success,
                timeoutInMs: 4000,
            })
        )
    }, [requestDestination, activeWorkspace, dispatch, selfId])

    return (
        <HighPriorityNovaModalContainer
            title="Request Access"
            closeModal={handleModalClose}
            onCancel={handleModalClose}
            className="w-[31.25rem]"
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    {requestDestination && (
                        <>
                            <CopyInviteLinkButton entityId={requestDestination.id} />
                            <div className="flex-grow" />
                        </>
                    )}
                    <ButtonTertiary title="Cancel" onClick={handleModalClose} />
                    <ButtonPrimary
                        onClick={() => {
                            handleModalClose()
                            handleAccessRequest()
                        }}
                        title="Request Access"
                    />
                </div>
            }
        >
            <div className="mt-6">
                <>
                    <h5 className="mb-2 text-gray-100">{t("Request edit access to")}</h5>
                    <ButtonWorkspaceDropdown
                        widthClass="w-full"
                        label={requestDestination?.fullName || dropdownButtons[0].label}
                        labelIcon={requestDestination?.icon}
                        menuOpen={dropdownOpen}
                        setMenuOpen={setDropdownOpen}
                        dropdownButtons={dropdownButtons}
                    />
                </>
            </div>
        </HighPriorityNovaModalContainer>
    )
}
